import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { setAuthExpiredHandler } from '../lib/api';
import { useNavigate, useLocation } from 'react-router-dom';

interface NetworkContextType {
  isOnline: boolean;
  isSessionExpired: boolean;
  dismissSessionExpired: () => void;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export function NetworkProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    setAuthExpiredHandler(() => setIsSessionExpired(true));
  }, []);

  const dismissSessionExpired = useCallback(() => {
    setIsSessionExpired(false);
    // Preserve current path so user can return after login
    navigate('/login', { state: { from: location.pathname } });
  }, [navigate, location.pathname]);

  return (
    <NetworkContext.Provider value={{ isOnline, isSessionExpired, dismissSessionExpired }}>
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetwork() {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}
